import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const mainLinks = [
  { label: 'Home', path: '/' },
  { label: 'About', path: '/about' },
  { label: 'Contact', path: '/contact' },
];

const shopLinks = [
  { label: 'T-Shirts', path: '/t-shirts' },
  { label: 'Magic Dresses', path: '/magic-dresses' },
  { label: 'Jackets', path: '/jackets' },
  { label: 'Scarves', path: '/scarves' },
  { label: 'Metal Drums', path: '/metal-drums' },
  { label: "Magic Dress How-To's", path: '/magic-dress-howtos' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const location = useLocation();

  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setShopOpen(false);
  }, [location.pathname]);

  // White text only on homepage when not scrolled; dark text everywhere else
  const useWhiteText = isHomePage && !scrolled;
  const textColor = useWhiteText ? '#fff' : '#4A3728';
  const isShopActive = shopLinks.some(l => location.pathname === l.path);

  // Navbar background: transparent only on homepage before scroll
  const navBg = scrolled ? '#FAF7F2' : isHomePage ? 'transparent' : '#FAF7F2';
  const navShadow = (scrolled || !isHomePage) ? '0 2px 12px rgba(0,0,0,0.08)' : 'none';

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{ backgroundColor: navBg, boxShadow: navShadow }}
    >
      <nav className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-24">
        {/* Logo — much larger */}
        <Link to="/" className="flex items-center flex-shrink-0">
          <img
            src="https://static.readdy.ai/image/577c45d6defa6ac02f4bf39c73856fa0/fbf245d3ea2c09ae3edd46f7fa5d3b47.webp"
            alt="Earth Wisdom™ Music and Clothing Logo"
            className="w-auto object-contain"
            style={{ height: '88px' }}
          />
        </Link>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-7">
          {mainLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className="text-xs font-bold tracking-widest uppercase transition-colors duration-200 whitespace-nowrap"
              style={{
                fontFamily: '"Lato", sans-serif',
                color: location.pathname === link.path ? '#C9933A' : textColor,
                borderBottom: location.pathname === link.path ? '2px solid #C9933A' : '2px solid transparent',
                paddingBottom: '2px',
              }}
            >
              {link.label}
            </Link>
          ))}

          {/* Shop Dropdown — fixed with padding bridge so mouse doesn't lose hover */}
          <div
            className="relative"
            onMouseEnter={() => setShopOpen(true)}
            onMouseLeave={() => setShopOpen(false)}
          >
            <button
              className="flex items-center gap-1 text-xs font-bold tracking-widest uppercase transition-colors duration-200 whitespace-nowrap cursor-pointer"
              style={{
                fontFamily: '"Lato", sans-serif',
                color: isShopActive ? '#C9933A' : textColor,
                borderBottom: isShopActive ? '2px solid #C9933A' : '2px solid transparent',
                paddingBottom: '2px',
                background: 'none',
                border: 'none',
                outline: 'none',
              }}
            >
              Shop <i className={`ri-arrow-down-s-line transition-transform duration-200 ${shopOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {/* Invisible bridge between button and dropdown — prevents gap from closing menu */}
            <div
              className="absolute left-0 right-0"
              style={{ top: '100%', height: '12px' }}
            />

            {shopOpen && (
              <div
                className="absolute left-0 rounded-xl overflow-hidden"
                style={{
                  top: 'calc(100% + 12px)',
                  backgroundColor: '#FAF7F2',
                  minWidth: '220px',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.14)',
                  border: '1px solid #EDE5D8',
                }}
              >
                {shopLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="block px-5 py-3.5 text-xs font-bold tracking-wide uppercase transition-colors duration-150 whitespace-nowrap hover:bg-[#EDE5D8]"
                    style={{
                      fontFamily: '"Lato", sans-serif',
                      color: location.pathname === link.path ? '#C9933A' : '#4A3728',
                    }}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <a
            href="tel:7346468620"
            className="flex items-center gap-1.5 text-xs font-bold whitespace-nowrap transition-all duration-200 px-4 py-2.5 rounded-lg hover:-translate-y-0.5"
            style={{ fontFamily: '"Lato", sans-serif', backgroundColor: '#C9933A', color: '#fff' }}
          >
            <i className="ri-phone-line"></i> 734.646.8620
          </a>
        </div>

        {/* Mobile Hamburger */}
        <button
          className="lg:hidden flex items-center justify-center w-10 h-10 rounded-lg transition-colors cursor-pointer"
          style={{ color: useWhiteText ? '#fff' : '#4A3728' }}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <i className={`text-2xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </nav>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="lg:hidden border-t border-[#EDE5D8] px-4 py-4 flex flex-col gap-1" style={{ backgroundColor: '#FAF7F2' }}>
          {mainLinks.map((link) => (
            <Link key={link.path} to={link.path}
              className="text-sm font-bold uppercase tracking-wide py-2.5 border-b border-[#EDE5D8] whitespace-nowrap"
              style={{ fontFamily: '"Lato", sans-serif', color: location.pathname === link.path ? '#C9933A' : '#4A3728' }}
            >{link.label}</Link>
          ))}
          <p className="text-xs font-bold uppercase tracking-widest pt-3 pb-1" style={{ color: '#C9933A', fontFamily: '"Lato", sans-serif' }}>Shop</p>
          {shopLinks.map((link) => (
            <Link key={link.path} to={link.path}
              className="text-sm font-bold uppercase tracking-wide py-2.5 border-b border-[#EDE5D8] pl-3 whitespace-nowrap"
              style={{ fontFamily: '"Lato", sans-serif', color: location.pathname === link.path ? '#C9933A' : '#4A3728' }}
            >{link.label}</Link>
          ))}
          <a href="tel:7346468620" className="flex items-center gap-2 font-bold text-sm mt-2 whitespace-nowrap" style={{ color: '#C9933A', fontFamily: '"Lato", sans-serif' }}>
            <i className="ri-phone-line"></i> 734.646.8620
          </a>
        </div>
      )}
    </header>
  );
}
